/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useMemo, useState } from "react";
import {
  closestCenter,
  DndContext,
  getFirstCollision,
  pointerWithin,
  PointerSensor,
  rectIntersection,
  useDroppable,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import {
  Button,
  Card,
  Col,
  Divider,
  Flex,
  Form,
  Input,
  Row,
  Space,
  Table,
  Tabs,
  Typography,
  message,
} from "antd";
import { ArrowLeftOutlined, PlusOutlined, SaveFilled } from "@ant-design/icons";
import { RiDeleteBin5Line } from "react-icons/ri";
import { useLocation, useNavigate } from "react-router";
import { delay } from "../../../utils/util.js";
import {
  componentsDraggableEditable,
  componentsEditable,
  buildMaterialColumns,
  ratioInKg,
  calcRawWeight,
  columnsParameterEditable,
  DEFAULT_PARAMETER_ROWS,
  PG,
} from "./model.js";
import BomService from "../../../service/ERP/BOM.service.js";
import MasterProcessService from "../../../service/ERP/MasterProcess.service.js";
import ModalMaterial from "../../../components/modal/ERP/material-no-pg-fg/ModalMaterial";

const masterProcessService = MasterProcessService();
const bomService = BomService();
const from = "/ERP/master-process";
const { Text, Title } = Typography;
const { TextArea } = Input;

const createRowId = (prefix) =>
  `${prefix}-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

const STEP_DROP_ZONE_PREFIX = "step-drop-zone-";

const createStepDropZoneId = (stepKey) => `${STEP_DROP_ZONE_PREFIX}${stepKey}`;

const getStepKeyFromDropZoneId = (dropZoneId) => {
  if (
    typeof dropZoneId !== "string" ||
    !dropZoneId.startsWith(STEP_DROP_ZONE_PREFIX)
  ) {
    return null;
  }

  return dropZoneId.slice(STEP_DROP_ZONE_PREFIX.length);
};

const createMaterialRow = (overrides = {}) => ({
  id: overrides.id || createRowId("material"),
  seq: 1,
  mat_code: "",
  mat_name: "",
  ratio: "0",
  multiply: "0",
  yield: "0",
  level: 1,
  typename: "",
  unit: "",
  method: "",
  ...overrides,
});

const createStepRow = (stepNo, overrides = {}) => ({
  key: overrides.key || createRowId("step"),
  stepNo,
  method: "",
  materials: [],
  ...overrides,
});

const resequenceMaterials = (materials = [], level = 1) =>
  materials.map((item, index) => {
    const normalized = createMaterialRow(item);
    return {
      ...normalized,
      seq: index + 1,
      level,
      /* ใช้สูตรเดียวกับน้ำหนักวัตถุดิบ : (น้ำหนักเข้าสูตร / multiply) / yield */
      wt_in_process: calcRawWeight(normalized),
    };
  });

const resequenceSteps = (steps = []) =>
  steps.map((step, index) => ({
    ...step,
    stepNo: index + 1,
    materials: resequenceMaterials(step.materials || [], index + 1),
  }));

const findMaterialLocation = (steps = [], materialId) => {
  for (let stepIndex = 0; stepIndex < steps.length; stepIndex += 1) {
    const materialIndex = (steps[stepIndex]?.materials || []).findIndex(
      (item) => item.id === materialId,
    );

    if (materialIndex >= 0) {
      return { stepIndex, materialIndex };
    }
  }

  return null;
};

const StepDropZone = ({ stepKey, isDragging, children }) => {
  const { isOver, setNodeRef } = useDroppable({
    id: createStepDropZoneId(stepKey),
  });

  let background = "transparent";
  let border = "2px dashed transparent";
  let boxShadow = "none";

  if (isOver) {
    background = "rgba(22, 119, 255, 0.10)";
    border = "2px dashed #1677ff";
    boxShadow = "0 0 0 3px rgba(22, 119, 255, 0.18) inset";
  } else if (isDragging) {
    background = "rgba(22, 119, 255, 0.03)";
    border = "2px dashed rgba(22, 119, 255, 0.35)";
  }

  return (
    <div
      ref={setNodeRef}
      style={{
        borderRadius: 8,
        minHeight: 80,
        position: "relative",
        transition: "background-color 0.15s ease, border-color 0.15s ease, box-shadow 0.15s ease",
        background,
        border,
        boxShadow,
      }}
    >
      {children}
      {isOver && (
        <div
          style={{
            position: "absolute",
            inset: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            pointerEvents: "none",
            zIndex: 10,
          }}
        >
          <div
            style={{
              background: "#1677ff",
              color: "#fff",
              borderRadius: 20,
              padding: "4px 16px",
              fontSize: 13,
              fontWeight: 600,
              opacity: 0.85,
              boxShadow: "0 2px 8px rgba(22,119,255,0.25)",
            }}
          >
            วางที่นี่
          </div>
        </div>
      )}
    </div>
  );
};

const buildStepsFromDetail = (detail = []) => {
  if (!detail.length) {
    return [createStepRow(1)];
  }

  const grouped = detail.reduce((map, item) => {
    const stepNo = Number(item?.stepno || item?.level || 1) || 1;
    if (!map.has(stepNo)) {
      map.set(stepNo, []);
    }
    map.get(stepNo).push(item);
    return map;
  }, new Map());

  const steps = Array.from(grouped.entries())
    .sort((a, b) => a[0] - b[0])
    .map(([stepNo, items]) =>
      createStepRow(stepNo, {
        materials: items.map((item, index) =>
          createMaterialRow({
            ...item,
            seq: index + 1,
            mat_code: item?.mat_code || item?.stcode || "",
            mat_name: item?.mat_name || item?.stname || "",
            typename: item?.typename || item?.sttype || "",
            multiply: item?.multiply ?? "0",
            yield: item?.yield ?? "0",
            level: stepNo,
          }),
        ),
      }),
    );

  return resequenceSteps(steps);
};

const buildDefaultStepFromBomDetail = (detail = []) => {
  if (!detail.length) {
    return [createStepRow(1)];
  }

  return [
    createStepRow(1, {
      materials: detail.map((item, index) =>
        createMaterialRow({
          ...item,
          seq: index + 1,
          mat_code: item?.mat_code || "",
          mat_name: item?.mat_name || "",
          typename: item?.typename || "",
          ratio: item?.qty_after_lost ?? "0",
          multiply: item?.multiply ?? "0",
          yield: item?.yield ?? "0",
          qty: item?.wt_in_process ?? item?.qty ?? "",
          level: 1,
        }),
      ),
    }),
  ];
};

const normalizeParameterRows = (rows = []) =>
  rows.map((item, index) => ({
    ...item,
    seq: index + 1,
    inspection: item?.inspection || item?.pk_code || "",
    spec: item?.spec || item?.pk_name || "",
  }));

const MasterProcessManage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { config } = location.state || { config: null };

  const [form] = Form.useForm();
  const [headerData, setHeaderData] = useState(PG);
  const [lost, setLost] = useState(0);
  const [steps, setSteps] = useState([createStepRow(1)]);
  const [parameters, setParameters] = useState(
    normalizeParameterRows(DEFAULT_PARAMETER_ROWS),
  );
  const [parametersProd, setParametersProd] = useState([]);
  const [openModalMaterial, setOpenModalMaterial] = useState(false);
  const [activeStepKey, setActiveStepKey] = useState(null);
  const [isDraggingMaterial, setIsDraggingMaterial] = useState(false);
  const [currentMpId, setCurrentMpId] = useState(null);
  const [currentVersion, setCurrentVersion] = useState(null);
  const [loadingVersion, setLoadingVersion] = useState(false);

  const saveMode = config?.action === "copy" ? "new" : "edit";

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { distance: 5 },
    }),
  );

  const collisionDetection = React.useCallback(
    (args) => {
      // Check if pointer is directly inside a StepDropZone
      const pointerCollisions = pointerWithin(args);
      const dropZoneHit = pointerCollisions.find(({ id }) =>
        typeof id === "string" && id.startsWith(STEP_DROP_ZONE_PREFIX),
      );

      if (dropZoneHit) {
        const stepKey = getStepKeyFromDropZoneId(dropZoneHit.id);
        const step = steps.find((s) => s.key === stepKey);
        const stepItemIds = (step?.materials || []).map((m) => m.id);

        // Empty step → drop directly on the zone
        if (stepItemIds.length === 0) {
          return [dropZoneHit];
        }

        // Non-empty step → find closest material row inside it
        const within = args.droppableContainers.filter((c) =>
          stepItemIds.includes(c.id),
        );
        if (within.length > 0) {
          return closestCenter({ ...args, droppableContainers: within });
        }

        return [dropZoneHit];
      }

      // Fallback: rect intersection first, then closest center
      const rectCollisions = rectIntersection(args);
      if (rectCollisions.length > 0) {
        return closestCenter({
          ...args,
          droppableContainers: args.droppableContainers.filter((c) =>
            rectCollisions.some((r) => r.id === c.id),
          ),
        });
      }

      return closestCenter(args);
    },
    [steps],
  );

  const flattenedDetails = useMemo(() => {
    return steps.flatMap((step) =>
      (step.materials || []).map((item, index) => ({
        ...item,
        mpno: index + 1,
        stcode: item?.stcode || item?.mat_code || "",
        stname: item?.stname || item?.mat_name || "",
        sttype: item?.sttype || item?.typename || "",
        method: item?.method || "",
        multiply: item?.multiply ?? "0",
        yield: item?.yield ?? "0",
        /* น้ำหนักวัตถุดิบ = (น้ำหนักเข้าสูตร / multiply) / yield */
        qty: calcRawWeight(item),
        stepno: step?.stepNo || 1,
      })),
    );
  }, [steps]);

  const summary = useMemo(() => {
    const safeLost = Number(lost || 0);

    return {
      materialCount: flattenedDetails.length,
      stepCount: steps.length,
    };
  }, [flattenedDetails, lost, steps.length]);

  const loadMasterProcess = async (mpId = null) => {
    try {
      setLoadingVersion(true);
      const res = await masterProcessService.get(config.fg_code, mpId);
      const {
        data: { header, detail, remark_step, parameters },
      } = res.data;

      let remarkStepMap = new Map();
      (remark_step || []).forEach((item) => {
        remarkStepMap.set(Number(item?.stepno || 0), item?.remark || "");
      });

      let nextSteps = buildStepsFromDetail(detail || []);

      if (!(detail || []).length) {
        try {
          const bomRes = await bomService.get(config.fg_code, "fg");
          const bomDetail = bomRes?.data?.data?.detail || [];
          nextSteps = buildDefaultStepFromBomDetail(bomDetail);
        } catch (bomError) {
          console.warn(bomError);
        }
      }

      nextSteps = nextSteps.map((step) => ({
        ...step,
        remark: remarkStepMap.get(step.stepNo) || "",
      }));

      setHeaderData({ ...PG, ...header });
      setLost(Number(header?.lost || 0));
      setSteps(nextSteps);
      {
        const qcRows = (parameters || []).filter(
          (p) => (p?.para_type || "QC") !== "PROD",
        );
        const prodRows = (parameters || []).filter(
          (p) => p?.para_type === "PROD",
        );
        setParameters(
          qcRows.length
            ? normalizeParameterRows(qcRows)
            : normalizeParameterRows(DEFAULT_PARAMETER_ROWS)
        );
        setParametersProd(normalizeParameterRows(prodRows));
      }
      setCurrentMpId(header?.mp_id || null);
      setCurrentVersion(header?.version || null);

      form.setFieldsValue({
        ...header,
        fg_code: header?.fg_code,
        fg_name: header?.fg_name,
        remark: header?.remark,
      });
    } catch (err) {
      console.warn(err);
      message.error("Error getting master process information.");
    } finally {
      setLoadingVersion(false);
    }
  };

  useEffect(() => {
    loadMasterProcess(config?.mp_id ?? null);

    return () => {
      form.resetFields();
    };
  }, []);

  const updateSteps = (updater) => {
    setSteps((current) =>
      resequenceSteps(
        typeof updater === "function" ? updater(current) : updater,
      ),
    );
  };

  const handleAddStep = () => {
    updateSteps((current) => [...current, createStepRow(current.length + 1)]);
  };

  const handleDeleteStep = (stepKey) => {
    updateSteps((current) => {
      const next = current.filter((step) => step.key !== stepKey);
      return next.length ? next : [createStepRow(1)];
    });
  };

  const handleStepRemarkChange = (stepKey, value) => {
    updateSteps((current) =>
      current.map((step) =>
        step.key === stepKey
          ? {
              ...step,
              remark: value,
            }
          : step,
      ),
    );
  };

  const handleOpenMaterialModal = (stepKey) => {
    setActiveStepKey(stepKey);
    setOpenModalMaterial(true);
  };

  const handleChooseMaterial = (material) => {
    if (!activeStepKey) {
      setOpenModalMaterial(false);
      return;
    }

    updateSteps((current) =>
      current.map((step) => {
        if (step.key !== activeStepKey) {
          return step;
        }

        return {
          ...step,
          materials: [
            ...(step.materials || []),
            createMaterialRow({
              ...material,
              unit: material?.unit || "",
              ratio: "1",
            }),
          ],
        };
      }),
    );

    setOpenModalMaterial(false);
    setActiveStepKey(null);
  };

  const handleMaterialFieldChange = (stepKey, materialId, field, value) => {
    updateSteps((current) =>
      current.map((step) => {
        if (step.key !== stepKey) {
          return step;
        }

        return {
          ...step,
          materials: (step.materials || []).map((item) => {
            if (item.id !== materialId) return item;
            const updated = { ...item, [field]: value };
            return updated;
          }),
        };
      }),
    );
  };

  const handleDeleteMaterial = (stepKey, materialId) => {
    updateSteps((current) =>
      current.map((step) => {
        if (step.key !== stepKey) {
          return step;
        }

        return {
          ...step,
          materials: (step.materials || []).filter(
            (item) => item.id !== materialId,
          ),
        };
      }),
    );
  };

  const handleSaveMaterial = (stepKey) => (row) => {
    const field = row?.key;
    const materialId = row?.id;

    if (!field || !materialId) {
      return;
    }

    const rawValue = row[field];
    const value = ["ratio", "carton", "multiply", "yield"].includes(field)
      ? rawValue || 0
      : rawValue;

    handleMaterialFieldChange(stepKey, materialId, field, value);
  };

  const handleMaterialDragStart = () => setIsDraggingMaterial(true);

  const handleMaterialDragEnd = ({ active, over }) => {
    setIsDraggingMaterial(false);
    if (!over || active.id === over.id) {
      return;
    }

    updateSteps((current) => {
      const source = findMaterialLocation(current, active.id);

      if (!source) {
        return current;
      }

      const sourceStep = current[source.stepIndex];
      const targetStepKey = getStepKeyFromDropZoneId(over.id);

      if (!targetStepKey) {
        const target = findMaterialLocation(current, over.id);

        if (!target) {
          return current;
        }

        if (source.stepIndex === target.stepIndex) {
          const materials = arrayMove(
            sourceStep.materials || [],
            source.materialIndex,
            target.materialIndex,
          );

          return current.map((step, index) =>
            index === source.stepIndex
              ? {
                  ...step,
                  materials,
                }
              : step,
          );
        }
      }

      const next = current.map((step) => ({
        ...step,
        materials: [...(step.materials || [])],
      }));

      const [movedMaterial] = next[source.stepIndex].materials.splice(
        source.materialIndex,
        1,
      );

      if (!movedMaterial) {
        return current;
      }

      let targetStepIndex = -1;
      let targetMaterialIndex = 0;

      if (targetStepKey) {
        targetStepIndex = next.findIndex((step) => step.key === targetStepKey);

        if (targetStepIndex < 0) {
          return current;
        }

        if (targetStepIndex === source.stepIndex) {
          targetMaterialIndex = next[targetStepIndex].materials.length;
        } else {
          targetMaterialIndex = next[targetStepIndex].materials.length;
        }
      } else {
        const target = findMaterialLocation(next, over.id);

        if (!target) {
          return current;
        }

        targetStepIndex = target.stepIndex;
        targetMaterialIndex = target.materialIndex;
      }

      next[targetStepIndex].materials.splice(
        targetMaterialIndex,
        0,
        movedMaterial,
      );
      return next;
    });
  };

  const handleDeleteParameter = (seq) => {
    setParameters((current) =>
      current
        .filter((item) => item.seq !== seq)
        .map((item, index) => ({
          ...item,
          seq: index + 1,
        })),
    );
  };

  const handleActionParameter = (record) =>
    parameters.length >= 1 ? (
      <Button
        className="bt-icon"
        size="small"
        danger
        icon={
          <RiDeleteBin5Line style={{ fontSize: "1rem", marginTop: "3px" }} />
        }
        onClick={() => handleDeleteParameter(record?.seq)}
        disabled={!record?.seq}
      />
    ) : null;

  const handleSaveParameter = (row) => {
    setParameters((current) => {
      const next = [...current];
      const index = next.findIndex((item) => item.seq === row.seq);
      if (index >= 0) {
        next.splice(index, 1, { ...next[index], ...row });
      }
      return next;
    });
  };

  const handleAddParameter = () => {
    setParameters((current) => [
      ...current,
      {
        seq: current.length + 1,
        inspection: "",
        spec: "",
      },
    ]);
  };

  /* ── Parameter ผลิต ── */
  const handleDeleteParameterProd = (seq) => {
    setParametersProd((current) =>
      current
        .filter((item) => item.seq !== seq)
        .map((item, index) => ({
          ...item,
          seq: index + 1,
        })),
    );
  };

  const handleActionParameterProd = (record) =>
    parametersProd.length >= 1 ? (
      <Button
        className="bt-icon"
        size="small"
        danger
        icon={
          <RiDeleteBin5Line style={{ fontSize: "1rem", marginTop: "3px" }} />
        }
        onClick={() => handleDeleteParameterProd(record?.seq)}
        disabled={!record?.seq}
      />
    ) : null;

  const handleSaveParameterProd = (row) => {
    setParametersProd((current) => {
      const next = [...current];
      const index = next.findIndex((item) => item.seq === row.seq);
      if (index >= 0) {
        next.splice(index, 1, { ...next[index], ...row });
      }
      return next;
    });
  };

  const handleAddParameterProd = () => {
    setParametersProd((current) => [
      ...current,
      {
        seq: current.length + 1,
        inspection: "",
        spec: "",
      },
    ]);
  };

  const handleBack = async () => {
    if (!config?.fg_code) {
      navigate(from, { replace: true });
    } else {
      navigate("/ERP/master-process/view", {
        state: {
          config: {
            title: "Master Process",
            action: "view",
            fg_code: config.fg_code,
          },
        },
        replace: true,
      });
    }
    await delay(300);
  };

  const handleConfirm = () => {
    form
      .validateFields()
      .then(() => {
        // if (!flattenedDetails.length) {
        //   throw new Error("กรุณาเพิ่มวัตถุดิบอย่างน้อย 1 รายการ");
        // }
        const header = {
          ...headerData,
          mpcode: form.getFieldValue("fg_code"),
          mpname: form.getFieldValue("fg_name"),
          remark: form.getFieldValue("remark"),
          mp_id: saveMode === "edit" ? currentMpId : null,
          save_mode: saveMode,
        };

        const parm = {
          header,
          detail: flattenedDetails,
          remark_step: steps.map((step) => ({
            stepno: step.stepNo,
            remark: step.remark,
          })),
          parameters: [
            ...parameters.map((item, index) => ({
              ...item,
              para_type: "QC",
              para_no: index + 1,
              inspection: item?.inspection || "",
              spec: item?.spec || "",
            })),
            ...parametersProd.map((item, index) => ({
              ...item,
              para_type: "PROD",
              para_no: index + 1,
              inspection: item?.inspection || "",
              spec: item?.spec || "",
            })),
          ],
        };

        masterProcessService
          .update(parm)
          .then(async () => {
            message.success("Request success.");
            navigate(from, { replace: true });
            await delay(300);
          })
          .catch((err) => {
            console.warn(err);
            const data = err?.response?.data;
            message.error(data?.message || "บันทึกไม่สำเร็จ");
          });
      })
      .catch((err) => {
        message.error(err?.message || "กรุณาตรวจสอบข้อมูลก่อนบันทึก");
      });
  };

  const parameterColumns = columnsParameterEditable(handleSaveParameter, {
    handleAction: handleActionParameter,
  });

  const parameterProdColumns = columnsParameterEditable(handleSaveParameterProd, {
    handleAction: handleActionParameterProd,
  });

  const sectionTop = (
    <Row
      gutter={[{ xs: 32, sm: 32, md: 32, lg: 12, xl: 12 }, 8]}
      className="m-0"
    >
      <Col span={12} className="p-0">
        <Flex gap={4} justify="start">
          <Button
            style={{ width: 120 }}
            icon={<ArrowLeftOutlined />}
            onClick={() => {
              handleBack();
            }}
          >
            กลับ
          </Button>
        </Flex>
      </Col>
      <Col span={12} style={{ paddingInline: 0 }}>
        <Flex gap={4} justify="end">
          <Button
            className="bn-center justify-center"
            icon={<SaveFilled style={{ fontSize: "1rem" }} />}
            type="primary"
            style={{ width: "9.5rem" }}
            onClick={() => {
              handleConfirm();
            }}
          >
            Save
          </Button>
        </Flex>
      </Col>
    </Row>
  );

  const mainTabContent = (
    <Space direction="vertical" size="middle" style={{ display: "flex" }}>
      <Card
        title={
          <Title style={{ marginTop: "15px" }} level={3}>
            {config?.title}
          </Title>
        }
      >
        <Space direction="vertical" size="middle" style={{ display: "flex" }}>
          <Card style={{ backgroundColor: "#f0f0f0" }}>
            <Divider orientation="left" className="!mb-3 !mt-1">
              ข้อมูลหลัก
            </Divider>
            <Row gutter={[8, 8]}>
              <Col xs={24} sm={24} md={12} lg={12} xl={8}>
                <Form.Item label="รหัสสินค้า" name="fg_code">
                  <Input placeholder="Enter Product Code." readOnly />
                </Form.Item>
              </Col>
              <Col xs={24} sm={24} md={12} lg={12} xl={8}>
                <Form.Item label="ชื่อสินค้า" name="fg_name">
                  <Input placeholder="Enter Product Name." readOnly />
                </Form.Item>
              </Col>
              <Col xs={24} sm={24} md={12} lg={12} xl={8}>
                <Form.Item label="เวอร์ชั่น Master Process" className="!mb-0">
                  <Text strong>
                    {currentVersion ? `เวอร์ชั่น ${currentVersion}` : "-"}
                  </Text>
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={[8, 8]}>
              <Col xs={24}>
                <Form.Item name="remark" label="Remark เพิ่มเติม">
                  <TextArea
                    rows={3}
                    placeholder="ระบุหมายเหตุเพิ่มเติมของกระบวนการผลิต"
                  />
                </Form.Item>
              </Col>
            </Row>
          </Card>
        </Space>
      </Card>

      <Card style={{ backgroundColor: "#f0f0f0" }}>
        <Divider orientation="left" className="!mb-3 !mt-1">
          ขั้นตอนการทำงานผลิต
        </Divider>
        <Flex justify="end" align="center" gap={12} wrap>
          <Button type="dashed" icon={<PlusOutlined />} onClick={handleAddStep}>
            เพิ่ม Step
          </Button>
        </Flex>
      </Card>

      <DndContext
        sensors={sensors}
        collisionDetection={collisionDetection}
        onDragStart={handleMaterialDragStart}
        onDragEnd={handleMaterialDragEnd}
      >
        <Space direction="vertical" size="middle" style={{ display: "flex" }}>
          {steps.map((step) => (
            <Card
              key={step.key}
              title={
                <Flex align="center" justify="space-between" gap={12} wrap>
                  <Title level={4} className="m-0 !text-zinc-800">
                    Step {step.stepNo}
                  </Title>
                  <Space>
                    <Button onClick={() => handleOpenMaterialModal(step.key)}>
                      เพิ่มวัตถุดิบ
                    </Button>
                    {step.key !== steps[0].key && (
                      <Button danger onClick={() => handleDeleteStep(step.key)}>
                        ลบ Step
                      </Button>
                    )}
                  </Space>
                </Flex>
              }
            >
              <Space
                direction="vertical"
                size="middle"
                style={{ display: "flex" }}
              >
                <StepDropZone stepKey={step.key} isDragging={isDraggingMaterial}>
                  <SortableContext
                    items={(step.materials || []).map((item) => item.id)}
                    strategy={verticalListSortingStrategy}
                  >
                    <Table
                      components={componentsDraggableEditable}
                      rowClassName={() => "editable-row"}
                      bordered
                      dataSource={step.materials}
                      columns={buildMaterialColumns(step, {
                        handleDeleteMaterial,
                        handleSave: handleSaveMaterial(step.key),
                        totalRatio: steps.reduce(
                          (sum, st) =>
                            sum +
                            (st.materials || []).reduce((a, m) => a + ratioInKg(m), 0),
                          0,
                        ),
                      })}
                      pagination={false}
                      rowKey="id"
                      scroll={{ x: "max-content" }}
                      size="small"
                      locale={{
                        emptyText:
                          "ลากมาวางที่ Step นี้ได้ หรือกดเพิ่มวัตถุดิบ",
                      }}
                    />
                  </SortableContext>
                </StepDropZone>
                <Form.Item
                  label={`การทำงานของ Step ${step.stepNo}`}
                  className="!mb-0"
                >
                  <TextArea
                    rows={3}
                    value={step.remark}
                    placeholder="อธิบายการทำงานของขั้นตอนนี้"
                    onChange={(event) =>
                      handleStepRemarkChange(step.key, event.target.value)
                    }
                  />
                </Form.Item>
              </Space>
            </Card>
          ))}
        </Space>
      </DndContext>
    </Space>
  );

  const parameterTabContent = (
    <Space direction="vertical" size="middle" style={{ display: "flex" }}>
      <Card style={{ backgroundColor: "#f0f0f0" }}>
        <Divider orientation="left" className="!mb-3 !mt-1">
          Parameter ผลิต
        </Divider>
        <Space direction="vertical" size="middle" style={{ display: "flex" }}>
          <Flex justify="end" align="center" gap={12} wrap>
            <Button
              type="dashed"
              icon={<PlusOutlined />}
              onClick={handleAddParameterProd}
            >
              เพิ่ม Parameter ผลิต
            </Button>
          </Flex>

          <Table
            title={null}
            components={componentsEditable}
            rowClassName={() => "editable-row"}
            bordered
            dataSource={parametersProd}
            columns={parameterProdColumns}
            pagination={false}
            rowKey="seq"
            scroll={{ x: "max-content" }}
            size="small"
          />
        </Space>
      </Card>

      <Card style={{ backgroundColor: "#f0f0f0" }}>
        <Divider orientation="left" className="!mb-3 !mt-1">
          Parameter QC
        </Divider>
        <Space direction="vertical" size="middle" style={{ display: "flex" }}>
          <Flex justify="end" align="center" gap={12} wrap>
            <Button
              type="dashed"
              icon={<PlusOutlined />}
              onClick={handleAddParameter}
            >
              เพิ่ม Parameter QC
            </Button>
          </Flex>

          <Table
            title={null}
            components={componentsEditable}
            rowClassName={() => "editable-row"}
            bordered
            dataSource={parameters}
            columns={parameterColumns}
            pagination={false}
            rowKey="seq"
            scroll={{ x: "max-content" }}
            size="small"
          />
        </Space>
      </Card>
    </Space>
  );

  return (
    <div className="bom-manage xs:px-0 sm:px-0 md:px-8 lg:px-8">
      <Space
        direction="vertical"
        size="middle"
        style={{
          display: "flex",
          position: "relative",
          paddingInline: "1.34rem",
        }}
        className="bom-modal"
      >
        {sectionTop}

        <Form form={form} layout="vertical" autoComplete="off">
          <Tabs
            defaultActiveKey="main"
            items={[
              {
                key: "main",
                label: "ข้อมูลหลัก",
                children: mainTabContent,
              },
              {
                key: "parameter",
                label: "Parameter",
                children: parameterTabContent,
              },
            ]}
          />
        </Form>

        {sectionTop}
      </Space>

      {openModalMaterial && (
        <ModalMaterial
          show={openModalMaterial}
          close={() => {
            setOpenModalMaterial(false);
            setActiveStepKey(null);
          }}
          values={(value) => {
            handleChooseMaterial(value);
          }}
        />
      )}
    </div>
  );
};

export default MasterProcessManage;
